import { useMemo } from 'react';
import { TRANSLATIONS, DEFAULT_LANGUAGE, EN_STRINGS } from './strings';

/**
 * Detects the language from URL query parameters
 * Looks for ?language=fr or ?language=en-US in the URL
 * Handles both simple codes (en, fr, de) and locale codes (en-US, fr-CA)
 *
 * @returns {string} Language code ('en', 'fr', or 'de')
 *
 * @example
 * // URL: https://chatbot.example.com?language=fr
 * getLanguageFromURL() // Returns: 'fr'
 *
 * // URL: https://chatbot.example.com?language=en-US
 * getLanguageFromURL() // Returns: 'en'
 *
 * // URL: https://chatbot.example.com (no param)
 * getLanguageFromURL() // Returns: 'en' (default)
 */
export function getLanguageFromURL() {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        let lang = urlParams.get('language');

        if (lang) {
            // Extract base language from locale codes like "en-US" -> "en"
            const baseLang = lang.split('-')[0].toLowerCase();

            // Validate the base language is supported
            if (TRANSLATIONS[baseLang]) {
                return baseLang;
            }
        }

        // Fallback to default language
        return DEFAULT_LANGUAGE;
    } catch (error) {
        console.error('[i18n] Error detecting language from URL:', error);
        return DEFAULT_LANGUAGE;
    }
}

/**
 * React hook to get translated strings based on URL language parameter
 *
 * Usage:
 * - RDM navigates to: https://chatbot.example.com?lang=fr
 * - Chatbot detects 'fr' and returns French strings
 *
 * @returns {object} Object containing all translated strings for the current language
 *
 * @example
 * function MyComponent() {
 *     const strings = useStrings();
 *     return <button>{strings.buttonStart}</button>;
 * }
 */
export function useStrings() {
    return useMemo(() => {
        const lang = getLanguageFromURL();
        const strings = TRANSLATIONS[lang] || EN_STRINGS;

        return strings;
    }, []); // Empty deps - only detect language once per component lifecycle
}
